import { useContext, useMemo } from 'react';
import clsx from 'clsx';

import CreateEditSchemaModalContext from '@atom/components/common/createEditSchemaModal/CreateEditSchemaModalContext';
import lineTypeIcon from '@atom/components/common/svgIcons/lineTypeIcon.svg';
import lineTypeIconBlue from '@atom/components/common/svgIcons/lineTypeIconBlue.svg';
import { Button, Icon, TextField } from '@atom/mui';
import colors from '@atom/styles/colors';
import { LocationType } from '@atom/types/schema';
import { getColorSwatchList } from '@atom/utilities/colorUtilities';
import { getSchemaIconsList } from '@atom/utilities/markerUtilities';

import classes from './createEditSchemaModal.module.css';

type LocationOption = {
  value: LocationType | null;
  label: string;
  renderIcon: (selected: boolean) => React.ReactNode;
};

const LOCATION_OPTIONS: LocationOption[] = [
  {
    value: null,
    label: 'No location',
    renderIcon: selected => (
      <Icon color={selected ? colors.brand.blue : colors.neutral.gray}>
        location_off
      </Icon>
    ),
  },
  {
    value: LocationType.POINT,
    label: 'Point',
    renderIcon: selected => (
      <Icon color={selected ? colors.brand.blue : colors.neutral.gray}>
        location_on
      </Icon>
    ),
  },
  {
    value: LocationType.LINE_STRING,
    label: 'Line',
    renderIcon: selected => (
      <img src={selected ? lineTypeIconBlue : lineTypeIcon} />
    ),
  },
];

const BasicDetailsTab = () => {
  const {
    name,
    setName,
    locationType,
    setLocationType,
    colorId,
    setColorId,
    setMarkerId,
  } = useContext(CreateEditSchemaModalContext);

  const isNoLocation = locationType === null;

  const handleTileClick = (index: number) => {
    setColorId(index);
    setMarkerId(index);
  };

  const swatches = useMemo(() => {
    const items = isNoLocation ? getColorSwatchList() : getSchemaIconsList();

    return items.map((item, index) => (
      <div
        key={index}
        className={clsx(
          classes.schemaIcon,
          index === colorId && classes.selected,
        )}
        onClick={() => handleTileClick(index)}
      >
        {isNoLocation ? (
          <Icon color={item} style={{ fontSize: '30px' }}>
            lens
          </Icon>
        ) : (
          <img src={item} />
        )}
      </div>
    ));
  }, [isNoLocation, colorId]);

  const swatchLabel = isNoLocation ? 'Color:' : 'Color & Marker:';

  return (
    <div className={classes.tabContainer}>
      <div className={classes.tabTitle}>Basic Details</div>
      <TextField
        label="Name"
        value={name}
        id="name"
        onChange={event => setName(event?.target?.value)}
        variant="standard"
        fullWidth
      />
      <div>
        <div className={classes.fieldLabel}>Location Type</div>
        <div className={classes.locationTypeRow}>
          {LOCATION_OPTIONS.map(option => {
            const selected = option.value === locationType;
            const color = selected ? colors.brand.blue : colors.neutral.dim;
            const backgroundColor = selected
              ? colors.utility.lightBlueHighlight
              : 'transparent';
            const hoverColor = selected
              ? colors.utility.lightBlueHighlight
              : colors.utility.highlight;

            return (
              <Button
                key={option.label}
                variant="text"
                startIcon={option.renderIcon(selected)}
                onClick={() => setLocationType(option.value)}
                sx={{
                  textTransform: 'none',
                  color,
                  backgroundColor,
                  '&:hover': {
                    backgroundColor: hoverColor,
                  },
                }}
              >
                {option.label}
              </Button>
            );
          })}
        </div>
      </div>
      <div>
        <div className={classes.fieldLabel}>{swatchLabel}</div>
        <div className={classes.colorContainer}>{swatches}</div>
      </div>
    </div>
  );
};

export default BasicDetailsTab;
